import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  Box,
  Typography,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import projectService from '../../services/projectService';
import meetingService from '../../services/meetingService';
import summaryService from '../../services/summaryService';

const ProjectMeetingsSelectionDialog = ({
  open,
  onClose,
  onConfirm,
  projectId,
  currentMeetingId
}) => {
  const [loading, setLoading] = useState(true);
  const [projectMeetings, setProjectMeetings] = useState([]);
  const [selectedMeetingIds, setSelectedMeetingIds] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [estimatedTokens, setEstimatedTokens] = useState(0);

  useEffect(() => {
    if (open && projectId) {
      loadProjectMeetings();
    }
  }, [open, projectId]);

  useEffect(() => {
    calculateEstimatedTokens();
  }, [selectedMeetingIds, projectMeetings]);

  const loadProjectMeetings = async () => {
    setLoading(true);
    try {
      const project = await projectService.getProjectWithMeetings(projectId);

      if (project && project.meetings) {
        const previousMeetings = project.meetings
          .filter(meeting => meeting.id !== currentMeetingId)
          .map(meeting => ({
            id: meeting.id,
            title: meeting.title,
            date: meeting.date,
            transcriptionId: meeting.transcriptionId,
            hasSummary: false,
            hasTranscription: !!meeting.transcriptionId,
            estimatedSize: 0
          }));

        for (const meeting of previousMeetings) {
          if (meeting.transcriptionId) {
            try {
              const summary = await summaryService.getSummaryByTranscriptionId(
                meeting.transcriptionId,
                { isAdmin: false }
              );
              if (summary && summary.status === 'completed') {
                meeting.hasSummary = true;
                meeting.estimatedSize = Math.ceil((summary.text || '').length / 4);
              }
            } catch (error) {
              console.warn(`Erreur récupération résumé pour ${meeting.id}`);
            }
          }
        }

        setProjectMeetings(previousMeetings);
        setSelectedMeetingIds(previousMeetings.map(m => m.id));
        setProjectInfo({
          name: project.name,
          description: project.description
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des réunions du projet:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimatedTokens = () => {
    const selected = projectMeetings.filter(m => selectedMeetingIds.includes(m.id));
    const total = selected.reduce((sum, meeting) => sum + (meeting.estimatedSize || 5000), 0);
    setEstimatedTokens(total);
  };

  const handleToggle = (meetingId) => {
    setSelectedMeetingIds(prev => {
      if (prev.includes(meetingId)) {
        return prev.filter(id => id !== meetingId);
      } else {
        return [...prev, meetingId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedMeetingIds(projectMeetings.map(m => m.id));
  };

  const handleDeselectAll = () => {
    setSelectedMeetingIds([]);
  };

  const handleConfirm = () => {
    onConfirm(selectedMeetingIds);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMMM yyyy', { locale: fr });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6">
            Contexte du Projet
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
              <AlertTitle>Génération avec contexte projet</AlertTitle>
              Le résumé sera généré en incluant l'historique des réunions sélectionnées ci-dessous.
              L'IA structurera automatiquement le résumé avec :
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <li>Un rappel du contexte du projet</li>
                <li>Un résumé des réunions antérieures</li>
                <li>Le résumé détaillé de la réunion actuelle</li>
              </Box>
            </Alert>

            {projectInfo && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Projet
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {projectInfo.name}
                </Typography>
                {projectInfo.description && (
                  <Typography variant="body2" color="text.secondary">
                    {projectInfo.description}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Réunions antérieures ({projectMeetings.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={handleSelectAll}>
                  Tout sélectionner
                </Button>
                <Button size="small" onClick={handleDeselectAll}>
                  Tout désélectionner
                </Button>
              </Box>
            </Box>

            {projectMeetings.length === 0 ? (
              <Alert severity="info">
                Aucune réunion antérieure dans ce projet.
                Le résumé sera généré sans contexte projet.
              </Alert>
            ) : (
              <List sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                {projectMeetings.map((meeting, index) => (
                  <React.Fragment key={meeting.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        py: 2,
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedMeetingIds.includes(meeting.id)}
                            onChange={() => handleToggle(meeting.id)}
                            disabled={!meeting.hasTranscription}
                          />
                        }
                        label={
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body1" fontWeight="medium">
                                {meeting.title}
                              </Typography>
                              {meeting.hasSummary && (
                                <Tooltip title="Résumé disponible">
                                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Chip
                                icon={<CalendarTodayIcon />}
                                label={formatDate(meeting.date)}
                                size="small"
                                variant="outlined"
                              />
                              {meeting.estimatedSize > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  ~{meeting.estimatedSize.toLocaleString()} tokens
                                </Typography>
                              )}
                            </Box>
                            {!meeting.hasTranscription && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                Transcription non disponible
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}

            {selectedMeetingIds.length > 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>{selectedMeetingIds.length}</strong> réunion(s) sélectionnée(s)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tokens estimés : <strong>~{estimatedTokens.toLocaleString()}</strong>
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
        >
          Continuer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectMeetingsSelectionDialog;
